import { ServiceSchema } from "@redotech/redo-fulfillment-carrier-common/carrier-service-catalog";
import { CarrierSchema } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { FulfillmentOrderAddressSchema } from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import { Provider } from "@redotech/redo-order-common/provider";
import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { DateFromDateOrIsoStringSchema } from "./date-schema";
import { RedoShopifyFulfillmentHoldReason } from "./fulfillment-hold-reason";
import { RedoShopifyOrderCancelReason } from "./order-cancel-reason";
import {
  PlacedOnHoldSource,
  ReleasedFromHoldSource,
} from "./placed-on-hold-source";
import { PrintableDocument } from "./print-job";
import { FulfillmentGroupReassignmentReasonSchema } from "./reassignment-reason";
import {
  SelectedShippingRateSetBy,
  SelectedShippingRateSetByInfoSchema,
} from "./selected-shipping-rate-set-by";

export enum OMSEventType {
  PACKING_SLIP_PRINTED = "packing_slip_printed",
  PACKING_SLIP_UNPRINTED = "packing_slip_unprinted",
  LABEL_PRINTED = "label_printed",
  LABEL_AND_PACKING_SLIP_PRINTED = "label_and_packing_slip_printed",
  PICK_LIST_PRINTED = "pick_list_printed",
  PICK_LIST_UNPRINTED = "pick_list_unprinted",
  PERSONALIZED_SLIP_PRINTED = "personalized_slip_printed",
  PERSONALIZED_SLIP_UNPRINTED = "personalized_slip_unprinted",
  ORDER_SUMMARY_PRINTED = "order_summary_printed",
  ORDER_SUMMARY_UNPRINTED = "order_summary_unprinted",
  MERGED = "merged",
  AUTO_MERGED = "auto_merged",
  UNMERGED = "unmerged",
  CREATED_FROM_SPLIT = "created_from_split",
  SPLIT = "split",
  AUTO_SPLIT = "auto_split",
  LABEL_PURCHASED = "label_purchased",
  LABEL_VOIDED = "label_voided",
  VERIFIED = "verified",
  UNVERIFIED = "unverified",
  READY_FOR_PICKUP_EMAIL_SENT = "ready_for_pickup_email_sent",
  LABEL_PURCHASED_EMAIL_SENT = "label_purchased_email_sent",
  ASSIGNED_TO_FULFILLMENT_SERVICE = "assigned_to_fulfillment_service",
  RESHIPPED = "reshipped",
  CREATED_FROM_RESHIP = "created_from_reship",
  MARKED_AS_FULFILLED = "marked_as_fulfilled",
  MANUAL_FULFILLMENT_CANCELLED = "manual_fulfillment_cancelled",
  MERCHANT_AUTO_SPLIT = "merchant_auto_split",
  CREATED_FROM_MERCHANT_AUTO_SPLIT = "created_from_merchant_auto_split",
  PACKING_SLIP_SCANNED = "packing_slip_scanned",
  /** @deprecated Use PlacedOnHoldEventSchema instead */
  PLACED_ON_HOLD_DUE_TO_ADDRESS_VERIFICATION_FAILURE = "placed_on_hold_due_to_address_verification_failure",
  SENT_EMAIL_TO_CUSTOMER_TO_VERIFY_ADDRESS = "sent_email_to_customer_to_verify_address",
  MARKED_AS_READY_FOR_PICKUP = "marked_as_ready_for_pickup",
  MARKED_AS_PICKED_UP = "marked_as_picked_up",
  OVERRODE_AS_RECEIVED = "overrode_as_received",
  RECEIVED_OVERRIDE_REVERTED = "received_override_reverted",
  AUTO_PRINTED = "auto_printed",
  ADDED_TO_PICK_SESSION = "added_to_pick_session",
  PICK_SESSION_CANCELLED = "pick_session_cancelled",
  MARKED_AS_PICKED = "marked_as_picked",
  MARKED_AS_PROBLEM = "marked_as_problem",
  ASSIGNED_TO_TOTE = "assigned_to_tote",
  SHIPPING_SERVICE_SELECTED = "shipping_service_selected",
  PLACED_ON_HOLD = "placed_on_hold",
  RELEASED_FROM_HOLD = "released_from_hold",
  TOTES_RELEASED = "totes_released",
  RELEASED_FOR_REPICK = "released_for_repick",
  ITEM_SCANNED = "item_scanned",
  ITEM_UNSCANNED = "item_unscanned",
  STALE_PICK_RELEASED = "stale_pick_released",
  MANUAL_SHIPMENT_CREATED = "manual_shipment_created",
  SHIPMENT_TAGS_UPDATED = "shipment_tags_updated",
  ORDER_CANCELLED = "order_cancelled",
  COMMENTSOLD_RECEIVED = "commentsold_received",
  COMMENTSOLD_NOT_RECEIVED = "commentsold_not_received",
  SHIPPING_ADDRESS_UPDATED = "shipping_address_updated",
  WORK_ORDER_STEP_ADVANCED = "work_order_step_advanced",
  PARCEL_PHOTO_CAPTURED = "parcel_photo_captured",
  PARCEL_PHOTO_REQUIREMENT_BYPASSED = "parcel_photo_requirement_bypassed",
  ORIGIN_ADDRESS_OVERRIDE_UPDATED = "origin_address_override_updated",
}

export enum ShippingAddressUpdateSource {
  MERCHANT_APP = "merchant_app",
  CUSTOMER_PORTAL = "customer_portal",
}

/** Code path that mutated `overrideOriginAddress`. */
export const OriginAddressOverrideSource = {
  MOVE: "move",
  REASSIGN: "reassign",
  MERGE: "merge",
  CREATE: "create",
  METADATA_COPY: "metadata-copy",
  LOCATION_ADDRESS_SYNC: "location-address-sync",
} as const;
export type OriginAddressOverrideSource =
  (typeof OriginAddressOverrideSource)[keyof typeof OriginAddressOverrideSource];

const BaseEventSchema = z
  .object({
    userId: zExt.objectId().nullish().meta({ privacy: privacy.id.internal }),
    createdAt: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.timestamp,
    }),
  })
  .meta({ privacy: privacy.structured });

const PackingSlipPrintedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.PACKING_SLIP_PRINTED),
  autoPrinted: z.boolean().meta({ privacy: privacy.flag }).nullish(),
});

const PackingSlipUnprintedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.PACKING_SLIP_UNPRINTED),
});

const LabelPrintedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.LABEL_PRINTED),
});

const LabelAndPackingSlipPrintedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.LABEL_AND_PACKING_SLIP_PRINTED),
});

const PickListPrintedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.PICK_LIST_PRINTED),
});

const PickListUnprintedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.PICK_LIST_UNPRINTED),
});

const PersonalizedSlipPrintedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.PERSONALIZED_SLIP_PRINTED),
});

const PersonalizedSlipUnprintedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.PERSONALIZED_SLIP_UNPRINTED),
});

const OrderSummaryPrintedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.ORDER_SUMMARY_PRINTED),
});

const OrderSummaryUnprintedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.ORDER_SUMMARY_UNPRINTED),
});

const BaseMergeEventSchema = BaseEventSchema.extend({
  mergeOrders: z.array(
    z.object({
      orderId: z.string().meta({ privacy: privacy.id.external.resource }),
      orderName: z.string().meta({ privacy: privacy.id.external.resource }),
    }),
  ),
});

const MergedEventSchema = BaseMergeEventSchema.extend({
  type: z.literal(OMSEventType.MERGED),
});

const UnmergedEventSchema = BaseMergeEventSchema.extend({
  type: z.literal(OMSEventType.UNMERGED),
});

const AutoMergedEventSchema = BaseMergeEventSchema.extend({
  type: z.literal(OMSEventType.AUTO_MERGED),
});

const CreatedFromSplitEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.CREATED_FROM_SPLIT),
  splitFromGroup: zExt.objectId(),
});

const SplitEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.SPLIT),
});

const AutoSplitEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.AUTO_SPLIT),
});

const LabelPurchasedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.LABEL_PURCHASED),
});

const LabelVoidedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.LABEL_VOIDED),
});

const VerifiedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.VERIFIED),
});

const UnverifiedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.UNVERIFIED),
});

const ReadyForPickupEmailSentEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.READY_FOR_PICKUP_EMAIL_SENT),
});

const LabelPurchasedEmailSentEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.LABEL_PURCHASED_EMAIL_SENT),
});

const AssignedToFulfillmentServiceEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.ASSIGNED_TO_FULFILLMENT_SERVICE),
  assignedTo: z.object({
    teamId: zExt.objectId(),
    teamName: z.string().meta({ privacy: privacy.resourceLabel }),
    fromAutomations: z.boolean().meta({ privacy: privacy.flag }),
  }),
  reassignmentReason: FulfillmentGroupReassignmentReasonSchema.nullish(),
  reassignmentNote: z
    .string()
    .meta({ privacy: privacy.text.merchant })
    .nullish(),
});

const ReshippedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.RESHIPPED),
  createdDuplicate: zExt.objectId(),
});

const CreatedFromReshipEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.CREATED_FROM_RESHIP),
  duplicateOf: zExt.objectId(),
});

const MerchantAutoSplitEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.MERCHANT_AUTO_SPLIT),
});

const CreatedFromMerchantAutoSplitEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.CREATED_FROM_MERCHANT_AUTO_SPLIT),
  splitFromGroup: zExt.objectId(),
});

const MarkedAsFulfilledEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.MARKED_AS_FULFILLED),
  notifyMarketplace: z.boolean().meta({ privacy: privacy.flag }).nullish(),
});

const ManualFulfillmentCancelledEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.MANUAL_FULFILLMENT_CANCELLED),
});

const PackingSlipScannedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.PACKING_SLIP_SCANNED),
});

/** @deprecated Use PlacedOnHoldDueToShippingServiceSelectionFailureEventSchema instead */
const PlacedOnHoldDueToAddressVerificationFailureEventSchema =
  BaseEventSchema.extend({
    type: z.literal(
      OMSEventType.PLACED_ON_HOLD_DUE_TO_ADDRESS_VERIFICATION_FAILURE,
    ),
  });

const SentEmailToCustomerToVerifyAddressEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.SENT_EMAIL_TO_CUSTOMER_TO_VERIFY_ADDRESS),
  templateId: z.string().meta({ privacy: privacy.id.internal }).nullish(),
});

const MarkedAsReadyForPickupEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.MARKED_AS_READY_FOR_PICKUP),
});

const MarkedAsPickedUpEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.MARKED_AS_PICKED_UP),
});

const OverrodeAsReceivedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.OVERRODE_AS_RECEIVED),
});

const ReceivedOverrideRevertedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.RECEIVED_OVERRIDE_REVERTED),
});

const AutoPrintedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.AUTO_PRINTED),
  documentType: createEnum(PrintableDocument, {
    enumName: "PrintableDocument",
  }),
});

const AddedToPickSessionEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.ADDED_TO_PICK_SESSION),
  pickSessionId: z.string().meta({ privacy: privacy.id.internal }),
  assignedPicker: zExt.objectId().nullish(),
});

const PickSessionCancelledEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.PICK_SESSION_CANCELLED),
  pickSessionId: z.string().meta({ privacy: privacy.id.internal }),
});

const MarkedAsPickedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.MARKED_AS_PICKED),
  pickSessionId: z.string().meta({ privacy: privacy.id.internal }).nullish(),
});

const MarkedAsProblemEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.MARKED_AS_PROBLEM),
  pickSessionId: z.string().meta({ privacy: privacy.id.internal }).nullish(),
  missingItemsCount: z.number().meta({ privacy: privacy.quantity }).nullish(),
});

const AssignedToToteEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.ASSIGNED_TO_TOTE),
  toteId: z.string().meta({ privacy: privacy.id.internal }),
  toteName: z.string().meta({ privacy: privacy.resourceLabel }),
  pickSessionId: z.string().meta({ privacy: privacy.id.internal }).nullish(),
});

const ShippingServiceSelectedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.SHIPPING_SERVICE_SELECTED),
  shippingService: z.object({ carrier: CarrierSchema, service: ServiceSchema }),
  selectedShippingRateSetBy: createEnum(SelectedShippingRateSetBy, {
    enumName: "SelectedShippingRateSetBy",
  }).nullish(),
  selectedShippingRateSetByInfo: SelectedShippingRateSetByInfoSchema.nullish(),
});

export const PlacedOnHoldEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.PLACED_ON_HOLD),
  source: createEnum(PlacedOnHoldSource, { enumName: "PlacedOnHoldSource" }),
  automationId: zExt.objectId().nullish(),
  automationName: z.string().meta({ privacy: privacy.resourceLabel }).nullish(),
  marketplaceSyncProvider: createEnum(Provider, {
    enumName: "Provider",
  }).nullish(),
  reason: createEnum(RedoShopifyFulfillmentHoldReason, {
    enumName: "RedoShopifyFulfillmentHoldReason",
  }).nullish(),
  reasonNotes: z.string().meta({ privacy: privacy.text.merchant }).nullish(),
});

export const ReleasedFromHoldEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.RELEASED_FROM_HOLD),
  source: createEnum(ReleasedFromHoldSource, {
    enumName: "ReleasedFromHoldSource",
  }),
});

const TotesReleasedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.TOTES_RELEASED),
  toteIds: z.array(z.string().meta({ privacy: privacy.id.internal })),
  toteNames: z.array(z.string().meta({ privacy: privacy.resourceLabel })),
});

const StalePickReleasedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.STALE_PICK_RELEASED),
  days: z.number().meta({ privacy: privacy.quantity }),
});

const ReleasedForRepickEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.RELEASED_FOR_REPICK),
});

export const ItemScannedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.ITEM_SCANNED),
  lineItemExternalId: z
    .string()
    .meta({ privacy: privacy.id.external.resource }),
  sku: z.string().meta({ privacy: privacy.productSku }).nullish(),
  title: z.string().meta({ privacy: privacy.productCatalogText }).nullish(),
  quantity: z.number().meta({ privacy: privacy.quantity }).nullish(),
});

export const ItemUnscannedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.ITEM_UNSCANNED),
  lineItemExternalId: z
    .string()
    .meta({ privacy: privacy.id.external.resource }),
  sku: z.string().meta({ privacy: privacy.productSku }).nullish(),
  title: z.string().meta({ privacy: privacy.productCatalogText }).nullish(),
  quantity: z.number().meta({ privacy: privacy.quantity }).nullish(),
});

const ManualShipmentCreatedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.MANUAL_SHIPMENT_CREATED),
});

const ShipmentTagsUpdatedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.SHIPMENT_TAGS_UPDATED),
  addedTagNames: z.array(z.string().meta({ privacy: privacy.resourceLabel })),
  removedTagNames: z.array(z.string().meta({ privacy: privacy.resourceLabel })),
});

const OrderCancelledEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.ORDER_CANCELLED),
  reason: createEnum(RedoShopifyOrderCancelReason, {
    enumName: "RedoShopifyOrderCancelReason",
  }).nullish(),
});

const CommentsoldReceivedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.COMMENTSOLD_RECEIVED),
});

const CommentsoldNotReceivedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.COMMENTSOLD_NOT_RECEIVED),
});

const ShippingAddressUpdatedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.SHIPPING_ADDRESS_UPDATED),
  previousAddress: FulfillmentOrderAddressSchema,
  newAddress: FulfillmentOrderAddressSchema,
  source: createEnum(ShippingAddressUpdateSource, {
    enumName: "ShippingAddressUpdateSource",
  }),
});

const WorkOrderStepAdvancedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.WORK_ORDER_STEP_ADVANCED),
  fromStep: z.string().meta({ privacy: privacy.resourceLabel }).nullish(),
  toStep: z.string().meta({ privacy: privacy.resourceLabel }).nullish(),
  workOrderId: z.string().meta({ privacy: privacy.id.internal }),
  sku: z.string().meta({ privacy: privacy.productSku }).nullish(),
});

/**
 * Fired when a parcel verification photo is captured + finalized on
 * the scan-to-verify page. The timeline renders this with a thumbnail
 * (the `url` is the public S3 URL) and the `source` indicator (scan /
 * manual / auto / file).
 */
const ParcelPhotoCapturedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.PARCEL_PHOTO_CAPTURED),
  photoId: z.string().meta({ privacy: privacy.id.internal }),
  url: z.string().meta({ privacy: privacy.publicUrl }),
  source: z.string().meta({ privacy: privacy.enumOrStatus }),
});

/**
 * Fired when an operator with PIN authority bypasses the parcel-photo
 * minimum-count requirement on the scan-to-verify page. `capturedCount`
 * / `minCount` record what the gate was at the moment of bypass so the
 * timeline preserves "how short of the requirement they were."
 */
const ParcelPhotoRequirementBypassedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.PARCEL_PHOTO_REQUIREMENT_BYPASSED),
  capturedCount: z.number().meta({ privacy: privacy.quantity }),
  minCount: z.number().meta({ privacy: privacy.quantity }),
});

/**
 * Fired on every mutation of `overrideOriginAddress`, including clears.
 * `source` names the code path that moved it (move, reassign, create, ...).
 */
const OriginAddressOverrideUpdatedEventSchema = BaseEventSchema.extend({
  type: z.literal(OMSEventType.ORIGIN_ADDRESS_OVERRIDE_UPDATED),
  source: z.string().meta({ privacy: privacy.enumOrStatus }).nullish(),
  previousLocationId: z
    .string()
    .meta({ privacy: privacy.id.external.resource })
    .nullish(),
  newLocationId: z
    .string()
    .meta({ privacy: privacy.id.external.resource })
    .nullish(),
  previousAddress: FulfillmentOrderAddressSchema.nullish(),
  newAddress: FulfillmentOrderAddressSchema.nullish(),
});

const OMSEventSchema_ = z
  .discriminatedUnion("type", [
    PackingSlipPrintedEventSchema.meta({ privacy: privacy.structured }),
    PackingSlipUnprintedEventSchema.meta({ privacy: privacy.structured }),
    LabelPrintedEventSchema.meta({ privacy: privacy.structured }),
    LabelAndPackingSlipPrintedEventSchema.meta({ privacy: privacy.structured }),
    PickListPrintedEventSchema.meta({ privacy: privacy.structured }),
    PickListUnprintedEventSchema.meta({ privacy: privacy.structured }),
    PersonalizedSlipPrintedEventSchema.meta({ privacy: privacy.structured }),
    PersonalizedSlipUnprintedEventSchema.meta({ privacy: privacy.structured }),
    OrderSummaryPrintedEventSchema.meta({ privacy: privacy.structured }),
    OrderSummaryUnprintedEventSchema.meta({ privacy: privacy.structured }),
    MergedEventSchema.meta({ privacy: privacy.structured }),
    UnmergedEventSchema.meta({ privacy: privacy.structured }),
    AutoMergedEventSchema.meta({ privacy: privacy.structured }),
    CreatedFromSplitEventSchema.meta({ privacy: privacy.structured }),
    SplitEventSchema.meta({ privacy: privacy.structured }),
    AutoSplitEventSchema.meta({ privacy: privacy.structured }),
    LabelPurchasedEventSchema.meta({ privacy: privacy.structured }),
    LabelVoidedEventSchema.meta({ privacy: privacy.structured }),
    VerifiedEventSchema.meta({ privacy: privacy.structured }),
    UnverifiedEventSchema.meta({ privacy: privacy.structured }),
    ReadyForPickupEmailSentEventSchema.meta({ privacy: privacy.structured }),
    LabelPurchasedEmailSentEventSchema.meta({ privacy: privacy.structured }),
    AssignedToFulfillmentServiceEventSchema.meta({
      privacy: privacy.structured,
    }),
    ReshippedEventSchema.meta({ privacy: privacy.structured }),
    CreatedFromReshipEventSchema.meta({ privacy: privacy.structured }),
    MerchantAutoSplitEventSchema.meta({ privacy: privacy.structured }),
    CreatedFromMerchantAutoSplitEventSchema.meta({
      privacy: privacy.structured,
    }),
    MarkedAsFulfilledEventSchema.meta({ privacy: privacy.structured }),
    ManualFulfillmentCancelledEventSchema.meta({ privacy: privacy.structured }),
    PackingSlipScannedEventSchema.meta({ privacy: privacy.structured }),
    PlacedOnHoldDueToAddressVerificationFailureEventSchema.meta({
      privacy: privacy.structured,
    }),
    SentEmailToCustomerToVerifyAddressEventSchema.meta({
      privacy: privacy.structured,
    }),
    MarkedAsReadyForPickupEventSchema.meta({ privacy: privacy.structured }),
    MarkedAsPickedUpEventSchema.meta({ privacy: privacy.structured }),
    OverrodeAsReceivedEventSchema.meta({ privacy: privacy.structured }),
    ReceivedOverrideRevertedEventSchema.meta({ privacy: privacy.structured }),
    AutoPrintedEventSchema.meta({ privacy: privacy.structured }),
    AddedToPickSessionEventSchema.meta({ privacy: privacy.structured }),
    PickSessionCancelledEventSchema.meta({ privacy: privacy.structured }),
    MarkedAsPickedEventSchema.meta({ privacy: privacy.structured }),
    MarkedAsProblemEventSchema.meta({ privacy: privacy.structured }),
    AssignedToToteEventSchema.meta({ privacy: privacy.structured }),
    ShippingServiceSelectedEventSchema.meta({ privacy: privacy.structured }),
    PlacedOnHoldEventSchema.meta({ privacy: privacy.structured }),
    ReleasedFromHoldEventSchema.meta({ privacy: privacy.structured }),
    TotesReleasedEventSchema.meta({ privacy: privacy.structured }),
    ReleasedForRepickEventSchema.meta({ privacy: privacy.structured }),
    ItemScannedEventSchema.meta({ privacy: privacy.structured }),
    ItemUnscannedEventSchema.meta({ privacy: privacy.structured }),
    StalePickReleasedEventSchema.meta({ privacy: privacy.structured }),
    ManualShipmentCreatedEventSchema.meta({ privacy: privacy.structured }),
    ShipmentTagsUpdatedEventSchema.meta({ privacy: privacy.structured }),
    OrderCancelledEventSchema.meta({ privacy: privacy.structured }),
    CommentsoldReceivedEventSchema.meta({ privacy: privacy.structured }),
    CommentsoldNotReceivedEventSchema.meta({ privacy: privacy.structured }),
    ShippingAddressUpdatedEventSchema.meta({ privacy: privacy.structured }),
    WorkOrderStepAdvancedEventSchema.meta({ privacy: privacy.structured }),
    ParcelPhotoCapturedEventSchema.meta({ privacy: privacy.structured }),
    ParcelPhotoRequirementBypassedEventSchema.meta({
      privacy: privacy.structured,
    }),
    OriginAddressOverrideUpdatedEventSchema.meta({
      privacy: privacy.structured,
    }),
  ])
  .meta({ privacy: privacy.structured });
export const OMSEventSchema: z.ZodType<OMSEvent> = OMSEventSchema_;
export type OMSEvent = {} & z.infer<typeof OMSEventSchema_>;

export const hiddenEvents = [
  OMSEventType.CREATED_FROM_SPLIT,
  OMSEventType.CREATED_FROM_RESHIP,
  OMSEventType.AUTO_SPLIT,
];
